// A segmented control (an ARIA radiogroup): one rounded track, the selected option raised onto
// surface 3 in the accent. Roving tabindex — only the selected option is a tab stop; arrows move
// and select, Home/End jump to the ends. Used by Settings, the feedback form and Tracking
// settings so every choice-of-few reads the same.
import { ui } from "../lib/telemetry/instrument.js";
import React from "react";

const CSS = `
.sy-seg{box-sizing:border-box;display:inline-flex;gap:2px;padding:2px;border:1px solid var(--c-border);border-radius:8px;background:var(--c-rowInactive);max-width:100%;}
.sy-seg-opt{flex:1 1 0;min-width:0;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:2px;padding:6px 14px;border-radius:6px;font-size:13px;font-weight:500;line-height:18px;color:var(--c-grayL);background:transparent;cursor:pointer;user-select:none;white-space:nowrap;transition:background .12s ease,color .12s ease;}
.sy-seg-opt:hover{color:var(--c-white);}
.sy-seg-opt[aria-checked="true"]{background:var(--c-rowActive);color:var(--c-accent);box-shadow:0 0 0 1px var(--c-border),0 1px 2px var(--c-shadow);}
.sy-seg-opt:focus-visible{outline:2px solid var(--c-accent);outline-offset:1px;}
.sy-seg-opt small{font-size:12px;font-weight:400;color:var(--c-gray);line-height:16px;}
.sy-seg--block{display:flex;width:100%;}
`;

/**
 * @param {object} props
 * @param {string} props.label           accessible name of the group
 * @param {{value: any, label: string, hint?: string, ui?: object}[]} props.options
 * @param {any} props.value              the selected option's value
 * @param {(value: any) => void} props.onChange
 * @param {boolean} [props.block]        stretch to the container's width
 */
export default function SegmentedControl({
  label,
  options,
  value,
  onChange,
  block = false,
  style,
}) {
  const sel = options.findIndex((o) => o.value === value);
  const onKey = (e, i) => {
    const n = options.length;
    let ni = -1;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") ni = (i + 1) % n;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") ni = (i - 1 + n) % n;
    else if (e.key === "Home") ni = 0;
    else if (e.key === "End") ni = n - 1;
    else if (e.key === "Enter" || e.key === " ") ni = i;
    else return;
    e.preventDefault();
    e.stopPropagation();
    onChange(options[ni].value);
    e.currentTarget.parentElement?.children[ni + 1]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={block ? "sy-seg sy-seg--block" : "sy-seg"}
      style={style}
    >
      <style>{CSS}</style>
      {options.map((o, i) => (
        <div
          key={String(o.value)}
          role="radio"
          aria-checked={i === sel}
          tabIndex={i === sel || (sel < 0 && i === 0) ? 0 : -1}
          className="sy-seg-opt"
          onClick={(e) => {
            e.stopPropagation();
            onChange(o.value);
          }}
          onKeyDown={(e) => onKey(e, i)}
          {...(o.ui && ui(o.ui["data-ui"], o.ui["data-ui-key"]))}
        >
          <span>{o.label}</span>
          {o.hint ? <small>{o.hint}</small> : null}
        </div>
      ))}
    </div>
  );
}
